/**
 * Просмотр ролика ЮСКАРа во вкладке DC «Газета».
 *
 * Один компонент на два случая:
 *  · номинант, уже стоящий в бюллетене — видно счёт голосов, можно привязать игру;
 *  · видео, ПРИСЛАННОЕ игроком — его надо посмотреть и решить, брать ли в номинацию.
 *
 * Вынесено из DcYuskarBlock: там модалка знала только про номинантов, и присланное
 * игроком видео нельзя было даже открыть — превью в списке не кликалось, решение
 * приходилось принимать по одному кадру постера.
 */
import { useState, type CSSProperties, type ReactElement } from "react";
import { NUM, box, capTitle, fmt } from "./dcVideoStatsParts";

export interface YuskarPreview {
  id: string;
  label: string;
  src: string;
  poster: string;
  game: string;
  bookingId: number;
  /** Номинант: счёт голосов. У присланного видео их ещё нет. */
  votes?: number;
  share?: number;
  durationSec?: number;
  /** Присланное игроком: id материала (по нему номинируем) и кто прислал. */
  materialId?: number;
  sender?: string;
  duration?: string;
  /** id номинаций, в которых это видео уже стоит. */
  takenIn?: string[];
}

interface Props {
  preview: YuskarPreview;
  nominations: { id: string; title: string }[];
  onClose: () => void;
  onNominate: (materialId: number, nominationId: string) => void | Promise<void>;
  onBindGame: (candidateId: string, bookingId: number) => void | Promise<void>;
}

const btn = (accent: boolean): CSSProperties => ({
  padding: "5px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer",
  border: "1px solid " + (accent ? "var(--role)" : "var(--surface-3)"),
  background: accent ? "color-mix(in srgb, var(--role) 16%, transparent)" : "transparent",
  color: accent ? "var(--text-1)" : "var(--text-2)",
});

export function DcYuskarPreview({ preview, nominations, onClose, onNominate, onBindGame }: Props): ReactElement {
  const [bindId, setBindId] = useState("");
  const [busy, setBusy] = useState("");
  const isSubmission = preview.materialId != null;
  const taken = new Set(preview.takenIn ?? []);

  const take = async (nominationId: string): Promise<void> => {
    if (preview.materialId == null) return;
    setBusy(nominationId);
    try { await onNominate(preview.materialId, nominationId); } finally { setBusy(""); }
  };

  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 40, display: "grid", placeItems: "center", padding: 12, background: "rgba(4,5,7,.82)" }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ ...box, width: "min(100%, 420px)", maxHeight: "90dvh", overflow: "auto", padding: 10, display: "grid", gap: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 800 }}>{preview.label}</span>
          <button type="button" onClick={onClose} aria-label="Закрыть"
            style={{ width: 26, height: 26, borderRadius: 6, border: "1px solid var(--surface-3)", background: "transparent", color: "var(--text-2)", cursor: "pointer" }}>✕</button>
        </div>

        {preview.src ? (
          <video controls autoPlay playsInline poster={preview.poster} src={preview.src}
            style={{ width: "100%", borderRadius: 8, background: "#000", maxHeight: "56dvh" }} />
        ) : (
          <div style={{ padding: 20, textAlign: "center", color: "var(--text-3)", fontSize: 12 }}>Файл недоступен</div>
        )}

        <div style={{ ...NUM, fontSize: 11.5, color: "var(--text-2)" }}>
          {isSubmission ? (
            <>Прислал: <b style={{ color: "var(--text-1)" }}>{preview.sender || "неопознанный отправитель"}</b>
              {preview.duration ? ` · ${preview.duration}` : ""}</>
          ) : (
            <>Голосов: <b style={{ color: "var(--text-1)" }}>{fmt(preview.votes ?? 0)}</b>
              {" · доля "}{fmt(preview.share ?? 0)}%
              {preview.durationSec ? ` · длина ${fmt(preview.durationSec)} сек` : ""}</>
          )}
        </div>

        {isSubmission ? (
          <div style={{ display: "grid", gap: 4 }}>
            <span style={{ ...capTitle, fontSize: 9.5 }}>Взять в номинацию</span>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {nominations.map((n) => {
                const already = taken.has(n.id);
                return (
                  <button key={n.id} type="button" disabled={already || busy === n.id}
                    title={already ? `Уже в номинации «${n.title}»` : `Взять в «${n.title}»`}
                    onClick={() => { void take(n.id); }}
                    style={{ ...btn(already), cursor: already ? "default" : "pointer" }}>
                    {already ? "✓ " : busy === n.id ? "… " : "+ "}{n.title}
                  </button>
                );
              })}
            </div>
            <span style={{ fontSize: 10.5, color: "var(--text-3)" }}>
              Игра у присланного видео известна из отчёта смены — указывать вручную не нужно.
            </span>
          </div>
        ) : null}

        <div style={{ display: "grid", gap: 4 }}>
          <span style={{ ...capTitle, fontSize: 9.5 }}>Игра</span>
          {preview.game
            ? <div style={{ ...NUM, fontSize: 12, color: "var(--text-1)" }}>
                {preview.game}{preview.bookingId ? <span style={{ color: "var(--text-3)" }}> · бронь {preview.bookingId}</span> : null}
              </div>
            : <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>
                Не привязана. Ролики нарезаны с камеры, поэтому игру нужно указать вручную — введи номер брони.
              </div>}
          {isSubmission ? null : (
            <div style={{ display: "flex", gap: 6 }}>
              <input value={bindId} onChange={(e) => setBindId(e.target.value.replace(/[^0-9]/g, ""))} placeholder="номер брони"
                style={{ flex: 1, minWidth: 0, padding: "5px 8px", borderRadius: 6, border: "1px solid var(--surface-3)", background: "var(--surface-2)", color: "var(--text-1)", fontSize: 12 }} />
              <button type="button" disabled={!bindId} onClick={() => { void onBindGame(preview.id, Number(bindId)); }}
                style={{ ...btn(true), cursor: bindId ? "pointer" : "default" }}>Привязать</button>
              {preview.bookingId ? (
                <button type="button" onClick={() => { void onBindGame(preview.id, 0); }} style={btn(false)}>Снять</button>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
